import { DateTime } from "luxon";

const FS2_EXPECTED_INTERVAL_SECONDS = 15 * 60;
const FS2_STALE_WINDOW_SECONDS = 20 * 60;
const CC_DEFAULT_INTERVAL_SECONDS = 10 * 60;
const CC_STALE_MULTIPLIER = 2;

const getFinitePositiveNumber = (value) => {
	const parsed = typeof value === "number" ? value : Number(value);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toEpochSeconds = (value) => {
	if (value === null || value === undefined || value === "") return null;

	if (typeof value === "number") {
		if (!Number.isFinite(value) || value <= 0) return null;
		return value > 1000000000000 ? Math.floor(value / 1000) : Math.floor(value);
	}

	if (typeof value === "string") {
		const trimmed = value.trim();
		if (!trimmed) return null;

		const numeric = Number(trimmed);
		if (Number.isFinite(numeric) && numeric > 0) {
			return numeric > 1000000000000 ? Math.floor(numeric / 1000) : Math.floor(numeric);
		}

		const dateTime = DateTime.fromISO(trimmed);
		if (dateTime.isValid) return Math.floor(dateTime.toSeconds());
	}

	return null;
};

// Shape test, used only when the device id does not identify the family.
// `temperature` used to be here and is not any more: CloudConnect firmware
// 1.2.x reports chip die temperature as telemetry, so every updated puck
// matched and was treated as an FS2 - expected every 15 minutes rather than on
// its own wake interval, and so shown offline about 18 minutes after each
// successful report. The remaining fields are genuinely FS2-only.
const isFs2Device = (reportedState = {}) =>
	Boolean(
		reportedState?.config ||
			reportedState?.flow ||
			reportedState?.valves ||
			reportedState?.outputs,
	);

//Exported so anything that only has the device list - the report device picker, say - can
//split a fleet by family without also loading every device's reported state.
export const getDeviceFamilyFromId = (deviceId) => {
	if (deviceId === null || deviceId === undefined) return null;

	const value = String(deviceId).trim().toUpperCase();
	if (!value) return null;
	if (value.startsWith("FS2_")) return "FS2";
	if (value.startsWith("ANDEL_") || value.startsWith("CC_")) return "CC";

	return null;
};

// The device id is authoritative - FS2_ against ANDEL_ or CC_ - so it is asked
// first. Sniffing the reported state is a guess, and a guess that goes wrong
// silently reclassifies a device and changes how long it is allowed to stay
// quiet. It is kept only for a device whose id follows neither convention.
const resolveDeviceFamily = ({ deviceId, reportedState }) => {
	const familyFromId = getDeviceFamilyFromId(deviceId);
	if (familyFromId) return familyFromId;
	return isFs2Device(reportedState) ? "FS2" : "CC";
};

export const getDevicePresence = ({ deviceId, reportedState, fallbackUpdatedAt, nowEpochSeconds }) => {
	const family = resolveDeviceFamily({ deviceId, reportedState });
	const lastSeenFromDevice = toEpochSeconds(reportedState?.device_timestamp);
	const lastSeenFromRecord = toEpochSeconds(fallbackUpdatedAt);
	const lastSeenAtSeconds = lastSeenFromDevice || lastSeenFromRecord;
	const lastSeenSource = lastSeenFromDevice
		? "device_timestamp"
		: lastSeenFromRecord
			? "record_updated_at"
			: null;

	const wakeIntervalSeconds = getFinitePositiveNumber(reportedState?.wake_interval);
	const keepaliveIntervalSeconds = getFinitePositiveNumber(reportedState?.keepalive_interval);

	const expectedIntervalSeconds =
		family === "FS2"
			? FS2_EXPECTED_INTERVAL_SECONDS
			: Math.max(wakeIntervalSeconds || 0, keepaliveIntervalSeconds || 0, CC_DEFAULT_INTERVAL_SECONDS);

	const staleAfterSeconds =
		family === "FS2" ? FS2_STALE_WINDOW_SECONDS : expectedIntervalSeconds * CC_STALE_MULTIPLIER;

	const nowSeconds = getFinitePositiveNumber(nowEpochSeconds)
		? Math.floor(nowEpochSeconds)
		: Math.floor(Date.now() / 1000);

	const ageSeconds =
		typeof lastSeenAtSeconds === "number" ? Math.max(0, nowSeconds - lastSeenAtSeconds) : null;

	const isOffline = ageSeconds !== null ? ageSeconds > staleAfterSeconds : false;

	return {
		family,
		lastSeenAtSeconds,
		lastSeenSource,
		wakeIntervalSeconds,
		keepaliveIntervalSeconds,
		expectedIntervalSeconds,
		staleAfterSeconds,
		ageSeconds,
		isOffline,
	};
};
